import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import NavBar from './components/NavBar';
import Homepage from './components/Homepage';
import BetaTestersPage from './components/BetaTestersPage';
import GatewayInstallationGuide, { GatewayInstallationContent } from './components/GatewayInstallationGuide';
import Dashboard from './components/Dashboard';
import Gateways from './components/Gateways';
import GatewayApprovalDashboard from './components/GatewayApprovalDashboard';
import AutoApprovalSettings from './components/AutoApprovalSettings';
import RegistrationRequestsPage from './components/RegistrationRequestsPage';
import Users from './components/Users';
import Settings from './components/Settings';
import Subscriptions from './components/Subscriptions';
import EditProfile from './components/EditProfile';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import SubscriptionPlans from './components/SubscriptionPlans';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import MySubscription from './components/MySubscription';
import SmartLanding from './components/SmartLanding';
import CompetitorResearch from './components/CompetitorResearch';
import ModelEvolutionBlogPage from './components/ModelEvolutionBlogPage';
import EyesenseFineTuningPlaybook from './components/EyesenseFineTuningPlaybook';
import UsbInstallerRoadmap from './components/UsbInstallerRoadmap';
import GoldenImageDeliverable from './components/GoldenImageDeliverable';
import OpenBetaAnnouncement from './components/OpenBetaAnnouncement';
import SharedDetectionPage from './components/SharedDetectionPage';
import { OperatorShippingPage } from './components/OperatorShippingGuide';
import { AuthProvider, useAuth } from './context/AuthContext';
import { trackPageView } from './analytics';
import {
  isSafeRedirect,
  postLoginPath,
  rememberClaimTokenFromLocation,
} from './utils/authRedirect';

function RedirectToLogin() {
  const location = useLocation();
  rememberClaimTokenFromLocation(location.pathname, location.search);
  const dest = `${location.pathname}${location.search}`;
  return <Navigate to={`/login?redirect=${encodeURIComponent(dest)}`} replace />;
}

function ContinueAfterAuth() {
  const location = useLocation();
  const dest = postLoginPath(location.search);
  if (isSafeRedirect(dest) || dest.startsWith('/gateways')) {
    return <Navigate to={dest} replace />;
  }
  return <SmartLanding />;
}

// Component to track page views in single-page app
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  return null;
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Track page views on route changes */}
      <Route path="*" element={<AnalyticsTracker />} />
      
      {/* Redirect authenticated users from homepage to smart landing */}
      <Route path="/" element={
        isAuthenticated ? <SmartLanding /> : <Homepage />
      } />
      
      {/* Public routes */}
      <Route path="/login" element={
        isAuthenticated ? <ContinueAfterAuth /> : <Login />
      } />
      <Route path="/register" element={
        isAuthenticated ? <ContinueAfterAuth /> : <Register />
      } />
      <Route path="/forgot-password" element={
        isAuthenticated ? <SmartLanding /> : <ForgotPassword />
      } />
      <Route path="/reset-password" element={
        isAuthenticated ? <SmartLanding /> : <ResetPassword />
      } />
      <Route path="/beta" element={<BetaTestersPage />} />
      {/* Public share landing - no auth required, no redirect when logged out */}
      <Route path="/share/:token" element={<SharedDetectionPage />} />
      <Route path="/install-gateway" element={<GatewayInstallationGuide />} />
      <Route path="/subscription" element={<Navigate to="/subscription/plans" replace />} />
      <Route path="/subscription/plans" element={
        isAuthenticated ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavBar />
            <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
              <SubscriptionPlans />
            </Box>
          </Box>
        ) : (
          <SubscriptionPlans />
        )
      } />
      <Route path="/blog/model-evolution" element={<ModelEvolutionBlogPage />} />
      <Route path="/factory/usb-installer" element={<UsbInstallerRoadmap />} />
      <Route path="/factory/golden-image" element={<GoldenImageDeliverable />} />
      <Route path="/open-beta" element={<OpenBetaAnnouncement />} />
      <Route path="/announcements/open-beta" element={<OpenBetaAnnouncement />} />
      <Route path="/__intel/eyesense-fine-tuning-playbook" element={<EyesenseFineTuningPlaybook />} />
      {/* Hidden internal pages - not linked from public site */}
      <Route path="/internal/market-research" element={<CompetitorResearch />} />
      
      {/* Protected routes - only accessible when authenticated */}
      {isAuthenticated ? (
        <>
          <Route path="/dashboard" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <Dashboard />
              </Box>
            </Box>
          } />
          <Route path="/gateways" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <Gateways />
              </Box>
            </Box>
          } />
          <Route path="/users" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <Users />
              </Box>
            </Box>
          } />
          <Route path="/settings" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <Settings />
              </Box>
            </Box>
          } />
          <Route path="/gateway-approvals" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <GatewayApprovalDashboard />
              </Box>
            </Box>
          } />
          <Route path="/registration-requests" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <RegistrationRequestsPage />
              </Box>
            </Box>
          } />
          <Route path="/auto-approval-settings" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <AutoApprovalSettings />
              </Box>
            </Box>
          } />
          <Route path="/subscriptions" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <Subscriptions />
              </Box>
            </Box>
          } />
          <Route path="/install-gateway-with-nav" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, mt: 0 }}>
                <GatewayInstallationContent showNavBar={true} />
              </Box>
            </Box>
          } />
          <Route path="/subscription/success" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <SubscriptionSuccess />
              </Box>
            </Box>
          } />
          <Route path="/my-subscription" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <MySubscription />
              </Box>
            </Box>
          } />
          <Route path="/profile" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <EditProfile />
              </Box>
            </Box>
          } />
          <Route path="/admin/shipping" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <NavBar />
              <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                <OperatorShippingPage />
              </Box>
            </Box>
          } />
        </>
      ) : (
        // Redirect unauthenticated users trying to access protected routes to login
        <>
          <Route path="/dashboard" element={<RedirectToLogin />} />
          <Route path="/gateways" element={<RedirectToLogin />} />
          <Route path="/users" element={<RedirectToLogin />} />
          <Route path="/settings" element={<RedirectToLogin />} />
          <Route path="/gateway-approvals" element={<RedirectToLogin />} />
          <Route path="/registration-requests" element={<RedirectToLogin />} />
          <Route path="/auto-approval-settings" element={<RedirectToLogin />} />
          <Route path="/subscriptions" element={<RedirectToLogin />} />
          <Route path="/subscription/success" element={<RedirectToLogin />} />
          <Route path="/my-subscription" element={<RedirectToLogin />} />
          <Route path="/profile" element={<RedirectToLogin />} />
          <Route path="/admin/shipping" element={<RedirectToLogin />} />
        </>
      )}
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
